import { z } from 'zod';
import { permissionSchema } from './permissions';

/** Rolle (ADR 0011). */
export const createRoleSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[a-z0-9_-]+$/, 'key darf nur Kleinbuchstaben, Ziffern, _ und - enthalten'),
  name: z.string().trim().min(1).max(120),
  permissions: z.array(permissionSchema).default([]),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    permissions: z.array(permissionSchema),
  })
  .partial();
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

export const roleResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  permissions: z.array(z.string()),
  isSystem: z.boolean(),
  /** true = die Rechte dieser Systemrolle wurden manuell angepasst (ADR 0038) —
   * der Seeder überschreibt sie danach nicht mehr bei jedem Neustart. */
  isCustomized: z.boolean(),
  memberCount: z.number().int(),
});
export type RoleResponse = z.infer<typeof roleResponseSchema>;

/** Benutzer inkl. zugewiesener Rollen (Admin-Ansicht). */
export const userResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string().nullable(),
  /** Benutzer-Kürzel (z. B. „MP") — Zuweisung/Zeichen in Aufgaben & Checklisten. */
  initials: z.string().nullable(),
  isActive: z.boolean(),
  roleIds: z.array(z.string().uuid()),
  roleNames: z.array(z.string()),
  lastSeenAt: z.string().datetime().nullable(),
});
export type UserResponse = z.infer<typeof userResponseSchema>;

/**
 * Schlanker User-Eintrag für Auswahlfelder (z. B. Sachbearbeiter am Beleg) —
 * bewusst ohne Rollen/E-Mail-Pflicht, für alle eingeloggten Nutzer abrufbar.
 */
export const userOptionSchema = z.object({
  id: z.string().uuid(),
  /** Anzeigename (Fallback: E-Mail). */
  name: z.string(),
  /** Benutzer-Kürzel (z. B. „MP"); null = nicht gepflegt. */
  initials: z.string().nullable(),
});
export type UserOption = z.infer<typeof userOptionSchema>;

/** Rollen eines Benutzers setzen (ersetzt die bestehende Zuordnung). */
export const setUserRolesSchema = z.object({
  roleIds: z.array(z.string().uuid()),
});
export type SetUserRolesInput = z.infer<typeof setUserRolesSchema>;

/** Stammdaten eines Benutzers ändern (Vier-Augen-Prinzip §11: offener Punkt). */
export const updateUserSchema = z.object({
  name: z.string().trim().min(1).max(200).nullable().optional(),
  email: z.string().trim().email().max(200).optional(),
  /** Benutzer-Kürzel, 1–8 Zeichen; null löscht es. */
  initials: z.string().trim().min(1).max(8).nullable().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const inviteUserSchema = z.object({
  email: z.string().trim().email().max(200),
  name: z.string().trim().max(200).optional(),
  roleIds: z.array(z.string().uuid()).default([]),
  /** Start-Passwort (ADR 0012); der Benutzer richtet beim ersten Login 2FA ein. */
  initialPassword: z.string().min(10, 'Mindestens 10 Zeichen.').max(200).optional(),
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

// ---- Vier-Augen-Freigabe für Rollenänderungen (ADR 0067) -------------------

export const roleChangeRequestStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'WITHDRAWN',
]);
export type RoleChangeRequestStatus = z.infer<typeof roleChangeRequestStatusSchema>;

/**
 * Antrag auf Rollenänderung. Rollen-/Rechteänderungen wirken nicht sofort — ein
 * ZWEITER Admin muss sie freigeben (CLAUDE.md §11). Ausnahme: existiert nur ein
 * einziger freigabeberechtigter Admin, greift die Änderung direkt (sonst könnte
 * sie nie jemand freigeben).
 */
export const roleChangeRequestResponseSchema = z.object({
  id: z.string().uuid(),
  targetUserId: z.string().uuid(),
  targetLabel: z.string(),
  requestedRoleIds: z.array(z.string().uuid()),
  requestedRoleNames: z.array(z.string()),
  previousRoleIds: z.array(z.string().uuid()),
  status: roleChangeRequestStatusSchema,
  requestedBy: z.string().uuid(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string().datetime(),
  decidedBy: z.string().uuid().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
});
export type RoleChangeRequestResponse = z.infer<typeof roleChangeRequestResponseSchema>;

/** Ablehnung eines Rollenantrags — Begründung ist Pflicht. */
export const rejectRoleChangeSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectRoleChangeInput = z.infer<typeof rejectRoleChangeSchema>;

/**
 * Ergebnis von „Rollen setzen": entweder direkt angewendet (Einzel-Admin-Fall)
 * oder als Antrag hinterlegt, der auf die Freigabe eines zweiten Admins wartet.
 */
export const setUserRolesResultSchema = z.object({
  applied: z.boolean(),
  user: userResponseSchema,
  request: roleChangeRequestResponseSchema.nullable(),
});
export type SetUserRolesResult = z.infer<typeof setUserRolesResultSchema>;
